import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import UIContainer from 'components/ui/UIContainer';
import { axiosInstance } from 'setup/axiosInstance';

type ExportFormat = 'CSV' | 'XLSX';
type ExportSection = 'WORDS' | 'IRREGULAR_VERBS';

const SECTION_LABELS: Record<ExportSection, string> = {
  WORDS: 'Мій словник слів',
  IRREGULAR_VERBS: 'Мій словник неправильних дієслів',
};

const getFilenameFromDisposition = (contentDisposition?: string) => {
  if (!contentDisposition) {
    return '';
  }

  const utfFilenameMatch = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)/,
  );
  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/);

  return decodeURIComponent(utfFilenameMatch?.[1] || filenameMatch?.[1] || '');
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const createFallbackFilename = (format: ExportFormat) => {
  const date = new Date().toISOString().slice(0, 10);

  return `my-vocabulary-${date}.${format.toLowerCase()}`;
};

const VocabularyExport: React.FC = () => {
  const { i18n } = useTranslation();
  const [format, setFormat] = useState<ExportFormat>('XLSX');
  const [sections, setSections] = useState<ExportSection[]>([
    'WORDS',
    'IRREGULAR_VERBS',
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const csvWithMultipleSections = format === 'CSV' && sections.length > 1;
  const canExport = sections.length > 0 && !csvWithMultipleSections && !loading;

  const helperText = useMemo(() => {
    if (csvWithMultipleSections) {
      return 'CSV підтримує експорт лише одного словника. Для обох словників оберіть XLSX.';
    }

    if (format === 'XLSX' && sections.length > 1) {
      return 'XLSX файл буде створено з окремими tabs для кожного словника.';
    }

    return '';
  }, [csvWithMultipleSections, format, sections.length]);

  const toggleSection = (section: ExportSection) => {
    setSections((prev) =>
      prev.includes(section)
        ? prev.filter((record) => record !== section)
        : [...prev, section],
    );
  };

  const handleExport = async () => {
    if (!canExport) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axiosInstance.post(
        '/vocabulary/export',
        {
          format,
          sections,
          locale: i18n.language,
        },
        { responseType: 'blob' },
      );

      const filename =
        getFilenameFromDisposition(response.headers['content-disposition']) ||
        createFallbackFilename(format);

      downloadBlob(response.data, filename);
    } catch (e) {
      setError('Не вдалося експортувати словник. Спробуйте ще раз.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <UIContainer sx={{ mb: 8 }}>
      <BreadCrumbs />

      <Stack spacing={3} sx={{ mt: 3 }}>
        <Box>
          <Typography variant={'h1'}>{'Експорт слів'}</Typography>
        </Box>

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Box>
                <Typography variant={'h3'} sx={{ mb: 1.5 }}>
                  {'Що експортувати'}
                </Typography>

                <Stack spacing={1}>
                  {(Object.keys(SECTION_LABELS) as ExportSection[]).map(
                    (section) => (
                      <FormControlLabel
                        key={section}
                        control={
                          <Checkbox
                            checked={sections.includes(section)}
                            onChange={() => toggleSection(section)}
                          />
                        }
                        label={SECTION_LABELS[section]}
                      />
                    ),
                  )}
                </Stack>
              </Box>

              <Box>
                <Typography variant={'h3'} sx={{ mb: 1.5 }}>
                  {'Формат файлу'}
                </Typography>

                <RadioGroup
                  row
                  value={format}
                  onChange={(event) =>
                    setFormat(event.target.value as ExportFormat)
                  }
                >
                  <FormControlLabel
                    value={'XLSX'}
                    control={<Radio />}
                    label={'XLSX'}
                  />
                  <FormControlLabel
                    value={'CSV'}
                    control={<Radio />}
                    label={'CSV'}
                  />
                </RadioGroup>
              </Box>

              {helperText ? (
                <Alert severity={'info'}>{helperText}</Alert>
              ) : null}
              {error ? <Alert severity={'error'}>{error}</Alert> : null}

              <Box>
                <Button
                  disabled={!canExport}
                  onClick={handleExport}
                  startIcon={<DownloadIcon />}
                >
                  {loading ? 'Експорт...' : 'Експортувати'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </UIContainer>
  );
};

export default VocabularyExport;
